import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import type { PropostaItem } from '@/types'
import type { CatalogPlan } from '@/utils/planCatalog'
import { Save, X, Settings, ListChecks } from 'lucide-react'
import { countIncludedServices } from './mergePlanServicesWithTemplate'
import { usePlanEditor } from './usePlanEditor'
import { PlanEditorDetailsTab } from './PlanEditorDetailsTab'
import { PlanEditorScopeTab } from './PlanEditorScopeTab'

export { DEFAULT_SERVICES_TEMPLATE } from './planEditorConstants'

interface PlanEditorProps {
  open: boolean
  plan: PropostaItem | null
  onSave: (updated: PropostaItem) => void
  onClose: () => void
  readonly?: boolean
  catalogPlans?: CatalogPlan[]
  guided?: boolean
}

export function PlanEditor({
  open,
  plan,
  onSave,
  onClose,
  readonly = false,
  catalogPlans = [],
  guided = false,
}: PlanEditorProps) {
  const s = usePlanEditor(open, plan, onSave, onClose, catalogPlans, guided)
  const isReadonly = readonly || !plan
  const includedCount = countIncludedServices(s.servicesList)

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isReadonly ? 'Detalhes do Plano' : 'Estúdio de Escopo & Valores'}
      description={
        isReadonly
          ? 'Informações completas do plano'
          : 'Configure o escopo de serviços, precificação e destaques visuais do PPT'
      }
      size="xl"
      className="!max-w-[850px] !max-h-[92vh]"
      footer={
        !isReadonly ? (
          <div className="flex items-center justify-between w-full border-t border-white/[0.06] pt-4 mt-2">
            <div className="text-left">
              <span className="text-[10px] text-text-label uppercase tracking-wider block">
                Total Geral Proposto
              </span>
              <span className="text-base font-bold text-accent">
                {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
                  s.proposedTotal,
                )}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" icon={<X size={14} />} onClick={onClose}>
                Cancelar
              </Button>
              <Button variant="primary" size="sm" icon={<Save size={14} />} onClick={s.handleSave}>
                Aplicar e Atualizar PPT
              </Button>
            </div>
          </div>
        ) : (
          <Button variant="secondary" size="sm" onClick={onClose}>
            Fechar
          </Button>
        )
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex border-b border-white/[0.06] p-0.5 bg-white/[0.02] rounded-lg">
          <button
            type="button"
            onClick={() => s.setActiveTab('details')}
            className={`flex-1 flex items-center justify-center gap-2 py-2 text-xs font-semibold rounded-md transition-all ${
              s.activeTab === 'details'
                ? 'bg-accent text-white shadow-lg'
                : 'text-text-muted hover:text-text-primary'
            }`}
          >
            <Settings size={14} />
            Configurações & Valores
          </button>
          <button
            type="button"
            onClick={() => s.setActiveTab('scope')}
            className={`flex-1 flex items-center justify-center gap-2 py-2 text-xs font-semibold rounded-md transition-all ${
              s.activeTab === 'scope'
                ? 'bg-accent text-white shadow-lg'
                : 'text-text-muted hover:text-text-primary'
            }`}
          >
            <ListChecks size={14} />
            Escopo de Entregáveis ({includedCount} Ativos)
          </button>
        </div>

        <div className="min-h-[420px] overflow-y-auto pr-1">
          {s.activeTab === 'details' ? (
            <PlanEditorDetailsTab
              isReadonly={isReadonly}
              descricao={s.descricao}
              onDescricaoChange={s.setDescricao}
              valorParcela={s.valorParcela}
              onValorParcelaChange={s.setValorParcela}
              valorEntrada={s.valorEntrada}
              onValorEntradaChange={s.setValorEntrada}
              duration={s.duration}
              onDurationChange={s.setDuration}
              valorTotal={s.valorTotal}
              showBonus={s.showBonus}
              onShowBonusChange={s.setShowBonus}
              bonusText={s.bonusText}
              onBonusTextChange={s.setBonusText}
              servicesList={s.servicesList}
              supportPricingMode={s.supportPricingMode}
              onSupportPricingModeChange={s.setSupportPricingMode}
              guided={guided}
            />
          ) : (
            <PlanEditorScopeTab
              isReadonly={isReadonly}
              servicesList={s.servicesList}
              onUpdateServiceField={s.updateServiceField}
              onToggleCategoryAll={s.toggleCategoryAll}
            />
          )}
        </div>
      </div>
    </Modal>
  )
}
